<!-- 
  @component
  ### Dropdown item
  a sub-component of `<DropdownMenu />`
  use to create a styled item in a dropdown menu, optional as a icon, text, radio or checkbox.  
  
  eg: https://www.figma.com/design/n0GA6zpBrMsZTHXS66c9rG/DS02.-Components?node-id=1331-79898&t=didmkVrYcbAG4GTf-4
-->
<script lang="ts">
  import FormControl from '$components/FormControl.svelte';
  import type { IconComponentType, IconWrapperSize, IconWrapperTheme } from '$mds/IconWrapper';
  import IconWrapper from '$mds/IconWrapper';
  import Image from '$mds/Image/Image.svelte';
  import { createEventDispatcher, getContext } from 'svelte';

  // bindings
  export let el: HTMLElement | undefined = undefined;

  // state
  export let isActive = false;
  export let variant: 'default' | 'danger' | 'disabled' = 'default';

  // layout
  // only in use if a default slot is not supplied
  export let label: string = '';
  export let href: string | null = null;
  export let type: 'radio' | 'checkbox' | 'text' = 'text';
  export let icon: IconComponentType | null = null;
  export let quantity: string | number | null = null;
  export let imgSrc: string | null = null;
  export let labelClasses = 'truncate';

  export let iconSize: IconWrapperSize = 'base';
  export let iconTheme: IconWrapperTheme = 'default';
  export let iconColor: string =
    variant === 'danger'
      ? 'text-alert-ondark-base'
      : variant === 'disabled'
        ? 'text-action-ondark-inactive'
        : 'text-ondark-secondary';
  export let closeOnClick: boolean = true;
  let className = '';
  export { className as class };

  const dispatch = createEventDispatcher();
  const closeMenuParent = getContext('closeDropdownOrSheet') as () => void;

  const classes = {
    default: `text-ondark-primary hover:bg-ondark-medium focus-visible:bg-ondark-high active:bg-ondark-high ${
      isActive ? 'bg-ondark-high' : ''
    }`,
    danger: `text-alert-ondark-base hover:bg-alert-ondark-subtle/50 focus-visible:bg-alert-ondark-subtle active:bg-alert-ondark-subtle ${
      isActive ? 'bg-alert-ondark-subtle' : ''
    }`,
    disabled: `text-action-ondark-inactive cursor-not-allowed`
  };

  function handlePress(e) {
    if (variant === 'disabled') return; // dont fire click event if disabled
    if (e.code === 'Space') e.preventDefault(); // prevent space from scrolling the page
    dispatch('click', e);
    closeOnClick && closeMenuParent?.();
  }
</script>

<svelte:element
  this={href ? 'a' : type === 'radio' || type === 'checkbox' ? 'label' : 'div'}
  bind:this={el}
  role={href ? 'link' : 'button'}
  href={href && variant !== 'disabled' ? href : null}
  {...href && href.match(/^(https?\:\/\/|www)/i) ? { target: '_blank', rel: 'noopener noreferrer' } : {}}
  tabindex={variant === 'disabled' ? -1 : 0}
  class="dropdown-menu-item {className} focus-visible:ring-ondark-focused group relative flex min-w-44 items-center rounded p-2 pr-4 outline-none ring-inset transition-all duration-200 focus-visible:rounded-lg focus-visible:ring-2 {classes[
    variant
  ]} gap-3"
  on:click={handlePress}
  on:keypress={handlePress}
  on:mouseenter
  on:mouseleave
  on:focus
  on:blur
  aria-disabled={variant === 'disabled'}
>
  {#if label}
    {#if type === 'radio' || type === 'checkbox'}
      <FormControl
        {type}
        checked={isActive}
        disabled={variant === 'disabled'}
        class="group-hover:[&:not(:checked)]:!border-ondark-active group-focus-visible:[&:not(:checked)]:!border-ondark-active !my-0"
        tabindex={-1}
        on:change
      />
    {/if}
    {#if icon}
      <IconWrapper {icon} size={iconSize} theme={iconTheme} class={iconColor} />
    {:else if imgSrc}
      <Image src={imgSrc} alt={label} class="h-8 w-8 rounded-full" />
    {/if}
    <span class={labelClasses}>{label}</span>
    {#if quantity !== null}
      <span class="text-ondark-subtle text-body-4xs ml-auto">
        {typeof quantity === 'number' ? `(${quantity})` : quantity}
      </span>
    {/if}
  {:else}
    <slot>Menu Item</slot>
  {/if}
</svelte:element>

<style lang="postcss">
  .dropdown-menu-item {
    & :global(input[type='radio']),
    & :global(input[type='checkbox']) {
      width: 1.5em;
      height: 1.5em;
    }
  }
</style>
